import { useEffect, createContext, memo, useCallback, useContext, useMemo, useState } from "react";
import type { APIUser } from "../types/user";
import { useAtomValue } from "jotai";
import { queryClientAtom } from "jotai-tanstack-query";
import { currentUserAtom } from "../atoms/user";
import { getApiInstance } from "../lib/api-provider/api-instance";

export enum ABILITY {
  can_create_tokens = "users.token.any",

  // webhooks
  can_change_webhooks = "webhooks.change",

  // projects
  can_delete_projects = "projects.delete",
  can_reset_project_cache = "projects.reset_cache",
  can_reset_dm_views = "views.reset",

  // Cloud Storage
  can_view_storage = "storages.view",
  can_manage_storage = "storages.change",
  can_sync_storage = "storages.sync",
}

export type Ability = ABILITY;

export type AuthPermissions = {
  can: (a: string) => boolean;
  canAny: (as: string[]) => boolean;
  canAll: (as: string[]) => boolean;
};

type AuthState = {
  user: APIUser | null;
  isLoading: boolean;
  refetch: () => void;
  update: (userUpdate: Partial<APIUser>) => Promise<APIUser | undefined>;
  permissions: AuthPermissions;
};

const AuthContext = createContext<AuthState | null>(null);

const LAST_USER_KEY = "afri:last-user";
/** In-memory last known user — survives refetch flashes without a
 *  localStorage read per render. Cleared only by a full page unload
 *  (sign-out navigates, so this can't leak across accounts; the
 *  storage fallback is token-gated anyway). */
let _lastUserMem: APIUser | null = null;
const readLastUser = (): APIUser | null => {
  try {
    const raw = window.localStorage.getItem(LAST_USER_KEY);
    return raw ? (JSON.parse(raw) as APIUser) : null;
  } catch {
    return null;
  }
};
const writeLastUser = (u: APIUser) => {
  try {
    window.localStorage.setItem(LAST_USER_KEY, JSON.stringify(u));
  } catch {
    /* quota / private mode */
  }
};
/** JWT ``exp`` check with base64url PADDING — Safari's ``atob`` is
 *  strict about it where Chrome is forgiving. */
const decodeExp = (token: string | null): number | null => {
  if (!token) return null;
  try {
    let b64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    const claims = JSON.parse(atob(b64)) as { exp?: number };
    return typeof claims.exp === "number" ? claims.exp : null;
  } catch {
    return null;
  }
};
const hasLiveTokens = (): boolean => {
  try {
    const now = Math.floor(Date.now() / 1000);
    const a = decodeExp(window.localStorage.getItem("afri:jwt:access"));
    const r = decodeExp(window.localStorage.getItem("afri:jwt:refresh"));
    return (a !== null && a > now) || (r !== null && r > now);
  } catch {
    return false;
  }
};
/** The ``user_id`` claim of the current access token (SimpleJWT's
 *  default subject claim). This is the account the CURRENT credential
 *  actually authenticates as, whoever the cached identity thinks we
 *  are. */
const currentTokenUserId = (): number | null => {
  try {
    const token = window.localStorage.getItem("afri:jwt:access");
    if (!token) return null;
    let b64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    const claims = JSON.parse(atob(b64)) as { user_id?: number; exp?: number };
    // An EXPIRED token proves nothing about who is here now. It is the
    // normal leftover state — someone's session lapsed, or a second
    // person opened the app on a shared machine — and it pairs with
    // that same person's cached identity, so the id check below finds
    // them perfectly consistent and the shell paints the PREVIOUS
    // account (staff navigation included) until the new sign-in's
    // whoami lands. Treat expiry as "no credential" so there is nothing
    // to fall back to.
    if (typeof claims.exp === "number" && claims.exp * 1000 <= Date.now()) return null;
    return typeof claims.user_id === "number" ? claims.user_id : null;
  } catch {
    return null;
  }
};
/** Cross-page-load identity cache — but ONLY when it belongs to the
 *  CURRENT credential. The cached user's id must equal the access
 *  token's ``user_id``; otherwise it is a DIFFERENT account left behind
 *  by a previous session (the token was overwritten at this login) and
 *  we ignore it. Without this, the boot fallback painted — and the
 *  route guard authorised against — the previous account during the
 *  whoami window: a signed-out staff user's cache briefly opened the
 *  Platform area for a following non-staff sign-in. With no readable
 *  token we cannot prove the cache belongs to us, so we withhold it and
 *  wait for the live whoami rather than risk the wrong identity. */
const readLastUserForCredential = (): APIUser | null => {
  const cached = readLastUser();
  if (!cached) return null;
  const sub = currentTokenUserId();
  if (sub == null) return null;
  return (cached as APIUser).id === sub ? cached : null;
};

const makePermissionChecker = (list?: (Ability | string)[]) => {
  const abilities = new Set<string>((list as string[]) ?? []);
  const has = (a: string) => {
    if (abilities.size === 0) return false;
    if (abilities.has(`-${a}`)) return false;
    if (abilities.has("*")) return true;
    return abilities.has(a);
  };
  return {
    can: (a: string) => has(a),
    canAny: (arr: string[]) => arr.some((a) => !abilities.has(`-${a}`) && (abilities.has("*") || abilities.has(a))),
    canAll: (arr: string[]) => arr.every((a) => !abilities.has(`-${a}`) && (abilities.has("*") || abilities.has(a))),
  };
};

export const AuthProvider = memo<{ children: React.ReactNode }>(({ children }) => {
  // User query — read-only.
  const userQuery = useAtomValue(currentUserAtom);
  const queryClient = useAtomValue(queryClientAtom);
  // Local "is updating" flag. We previously read this from
  // ``currentUserUpdateAtom.isPending``, but going through
  // jotai-tanstack-query's ``atomWithMutation`` has a stale-state
  // issue: ``useAtomValue`` can return a mutation observer whose
  // ``state.variables`` got reset by a prior render, and a follow-
  // up ``mutateAsync(args)`` call lands at ``mutationFn`` with
  // ``undefined``. Switch to a direct ``api.invoke()`` and a local
  // useState for the busy flag — same UX, no atom-observer race.
  const [isUpdating, setIsUpdating] = useState(false);

  // User functions
  const refetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["current-user"] });
  }, [queryClient]);

  const update = useCallback(
    async (userUpdate: Partial<APIUser>) => {
      if (!userQuery.data || userQuery.data.id == null) {
        console.error("User is not loaded. Try fetching first.");
        return undefined;
      }
      setIsUpdating(true);
      try {
        const api = getApiInstance();
        const result = await api.invoke<APIUser>(
          "updateUser",
          { pk: userQuery.data.id },
          { body: userUpdate, errorFilter: () => true },
        );
        // Refresh ``me`` so the rest of the UI sees the new values.
        queryClient.invalidateQueries({ queryKey: ["current-user"] });
        return result;
      } finally {
        setIsUpdating(false);
      }
    },
    [userQuery.data, queryClient],
  );

  // Permissions
  const checker = useMemo(() => makePermissionChecker(userQuery.data?.permissions), [userQuery.data?.permissions]);
  const permissionHelpers = useMemo<AuthPermissions>(() => {
    return {
      can: (a: string) => checker.can(String(a)),
      canAny: (abilities: string[]) => checker.canAny(abilities.map((a) => String(a))),
      canAll: (abilities: string[]) => checker.canAll(abilities.map((a) => String(a))),
    };
  }, [checker]);

  useEffect(() => {
    const data = userQuery.data as APIUser | undefined;
    if (!data || data.id == null) return;
    const uid = currentTokenUserId();
    if (uid != null && data.id !== uid) {
      // Stale / wrong-account cache — the previous session's identity is
      // still warm after a client-side login (passkey / account switch
      // without a reload). Do NOT poison the last-known caches with it;
      // force a refetch so the correct identity replaces it and the
      // shell shows a loading state in the meantime.
      queryClient.invalidateQueries({ queryKey: ["current-user"] });
      return;
    }
    _lastUserMem = data;
    writeLastUser(data);
  }, [userQuery.data, queryClient]);

  // Self-heal: while we hold live tokens but no identity (the boot whoami
  // failed — slow network, cold cloud start, first launch after an
  // update), re-ask whenever connectivity is confirmed and on a slow
  // poll, instead of waiting for a window-focus or an app restart.
  const hasIdentity = !!(userQuery.data && (userQuery.data as APIUser).id != null);
  useEffect(() => {
    if (hasIdentity || typeof window === "undefined") return undefined;
    const kick = () => {
      if (hasLiveTokens() || !(typeof (window as any).Capacitor !== "undefined")) {
        queryClient.invalidateQueries({ queryKey: ["current-user"] });
      }
    };
    window.addEventListener("afri:online-confirmed", kick);
    window.addEventListener("online", kick);
    const timer = window.setInterval(kick, 15000);
    return () => {
      window.removeEventListener("afri:online-confirmed", kick);
      window.removeEventListener("online", kick);
      window.clearInterval(timer);
    };
  }, [hasIdentity, queryClient]);

  const contextValue: AuthState = useMemo(() => {
    // Prefer cached data over success-only gating so a refetch error
    // (typical when the device goes offline mid-session) doesn't
    // suddenly flip ``user`` to null. Tanstack keeps the prior
    // payload in ``data`` even when the latest fetch failed; we
    // surface it here so the menubar identity row, sidebar org
    // header, and useEnsureActiveOrg all keep working from the last
    // known good state. Falls back to ``null`` only when we've
    // never had a successful fetch.
    // Last-known identity: the fresh ``me`` fetch can fail on a slow
    // or flaky boot (it is deliberately NOT mirror-first — identity
    // must be fresh when it can be), which left ``user`` null while
    // every mirror-served page rendered → "logged out but the home
    // page is visible". If tokens are still live, the previous
    // successful payload is the truth until the next fetch lands.
    // While a REFETCH is in flight the query atom can briefly report
    // ``data: undefined`` — flashing ``user`` to null unmounted every
    // layout gated on it (measured: platform pages blanked ~1s when a
    // mirror update invalidated queries). Keep the last known identity
    // whenever the tokens are still live; only a real sign-out (dead
    // tokens) nulls the user.
    // JWT hosts (Capacitor/desktop): the fallback requires LIVE tokens
    // so a local sign-out (tokens cleared, SPA not reloaded) never
    // resurrects the previous identity. Web signs out via a full page
    // navigation, which wipes module state — the mem copy can't leak.
    const jwtHost = typeof window !== "undefined" && typeof (window as any).Capacitor !== "undefined";
    const fallbackAllowed = jwtHost ? hasLiveTokens() : true;
    // The LIVE query result is authoritative ONLY when it belongs to the
    // current credential. After a login / account-switch WITHOUT a full
    // page reload (e.g. passkey sign-in, which navigates client-side),
    // the ``["current-user"]`` cache still holds the PREVIOUS account's
    // payload for its staleTime — rendering that account's identity,
    // roles and nav until a background refetch lands (measured ~10s
    // after a passkey switch: "abumafrim" shown before "afriannotate").
    // Bind it to the access token's ``user_id`` (same rule as the
    // fallback); a mismatch means the cache is stale/wrong-account, so
    // we drop it here and the effect below forces an immediate refetch —
    // the shell shows a loading state, never the wrong account.
    const tokenUid = currentTokenUserId();
    const liveUser =
      userQuery.data && (tokenUid == null || (userQuery.data as APIUser).id === tokenUid)
        ? (userQuery.data as APIUser)
        : undefined;
    // Every fallback identity is credential-bound too. ``_lastUserMem``
    // is a MODULE-level cache that survives a client-side login (no
    // reload), so after a passkey account-switch it can still hold the
    // previous account — bind it to the token like the others.
    // ``readLastUserForCredential`` already checks the cross-load
    // localStorage cache. When nothing matches the current credential,
    // ``user`` is null and the shell shows a loading state until the
    // fresh whoami lands — never the wrong account.
    const memUser =
      _lastUserMem && (tokenUid == null || (_lastUserMem as APIUser).id === tokenUid) ? _lastUserMem : null;
    const user = liveUser ?? (fallbackAllowed ? (memUser ?? readLastUserForCredential()) : null);
    return {
      user: user ?? null,
      isLoading: userQuery.isFetching || isUpdating,
      refetch,
      update,
      permissions: permissionHelpers,
    };
  }, [userQuery.isSuccess, userQuery.data, userQuery.isFetching, isUpdating, refetch, update, permissionHelpers]);

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
});

export const useAuth = () => {
  const ctx = useContext(AuthContext)!;

  return {
    user: ctx.user,
    isLoading: ctx?.isLoading ?? false,
    refetch: ctx.refetch,
    update: ctx.update,
    permissions: ctx.permissions,
  };
};
